import {inject, Injectable, signal, Signal, WritableSignal} from '@angular/core';
import {NavigationEnd, NavigationExtras, QueryParamsHandling, Router} from '@angular/router';
import {defer, Observable} from 'rxjs';
import {filter, map, startWith} from 'rxjs/operators';
import {AppQuery} from '../../queries/app.query';
import {HistoryService} from '../history/history.service';
import {HistoryItem} from '../../models/history/history-item.model';
import {BrowserQuery} from '../../queries/browser.query';
import {AuthQuery} from '../../queries/auth.query';
import {NavigationError} from '../../errors/navigation-error';
import {IconService} from '../icon/icon.service';
import {GetModifiedUrlSegmentsParams} from '../../models/get-modified-url-segments-params-interface.model';
import {URL_PATH_CONFIGS} from '../../../configs/url-path.config';
import {VaultQuery} from '../../queries/vault.query';
import {StoredSearchItem} from '../../models/search/stored-search-item';
import {AppService} from '../app/app.service';
import {SearchQuery as ApiSearchQuery} from '../../api/models/search-query';
import {SearchQueryVisibilityEnum} from '../../enums/search-query-visibility.enum';
import {ICONS} from '../../constants/icons/icons.constants';
import {SearchScopeEnum} from '../../enums/search-scope.enum';
import {Vault} from '../../api/models/vault';
import {TranslateService} from '@ngx-translate/core';
import {TabIdEnum} from '../../enums/tab-id.enum';
import {ClickFinderNodeWithIcon} from '../../models/click-finder-node-with-icon.model';

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private readonly appQuery = inject(AppQuery);
    private readonly appService = inject(AppService);
    private readonly authQuery = inject(AuthQuery);
    private readonly browserQuery = inject(BrowserQuery);
    private readonly historyService = inject(HistoryService);
    private readonly translateService = inject(TranslateService);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly router = inject(Router);
    private readonly afterLoginNavigation: WritableSignal<boolean> = signal(false);
    private readonly urlSeparator = '/';
    private readonly responsiveUrlPaths: string[] = [
        URL_PATH_CONFIGS.paths.auth,
        URL_PATH_CONFIGS.paths.clickFinder,
        URL_PATH_CONFIGS.paths.favorites,
        URL_PATH_CONFIGS.paths.lastEdited,
        URL_PATH_CONFIGS.paths.magnets,
        URL_PATH_CONFIGS.paths.newestDocuments,
        URL_PATH_CONFIGS.paths.search,
        URL_PATH_CONFIGS.paths.searchQuery
    ];

    public async navigate(
        url: string | Array<string | undefined>,
        navigationExtras?: NavigationExtras,
        forceReplace: boolean = false,
        historyItem?: HistoryItem,
    ): Promise<boolean> {
        let newUrl: Array<string> = [];

        if (typeof url === 'string') {
            newUrl.push(url);
        } else {
            url.forEach(part => {
                newUrl.push(part as string);
            });
        }

        // if the first element is /, navigate directly
        if (url[0] === this.urlSeparator) {
            return await this.triggerNavigation(newUrl, navigationExtras, historyItem);
        }

        newUrl = this.getModifiedUrlSegments({
            urlSegments: newUrl,
            hasSmallLayout: this.browserQuery.hasSmallViewport()
        });

        // make sure to only replace urls with the same target page (without the id at the end)
        if (navigationExtras?.replaceUrl && !forceReplace) {
            const currentUrl = location.pathname.substring(1)
                .split(this.urlSeparator);
            currentUrl.pop();
            const target = [...newUrl];
            target.pop();
            if (JSON.stringify(currentUrl) !== JSON.stringify(target)) {
                delete navigationExtras.replaceUrl;
            }
        }

        return await this.triggerNavigation(newUrl, navigationExtras, historyItem);
    }

    public goBackInHistory(): void {
        let lastNavigation = this.appQuery.getLastNavigationHistoryItem();
        if (!lastNavigation) {
            return;
        }

        // Check if last navigation is current route. Then go back two items.
        const lastNavigationUrl = this.getLastNavigationUrl(lastNavigation.path, lastNavigation.queryParams);
        const currentUrl = this.getCurrentUrl();
        if (lastNavigationUrl === currentUrl) {
            this.historyService.removeLatestHistoryItem();
            lastNavigation = this.appQuery.getLastNavigationHistoryItem();

            if (!lastNavigation) {
                return;
            }
        }

        this.navigate(lastNavigation.path,
                {
                    queryParams: lastNavigation.queryParams,
                    queryParamsHandling: 'replace'
                })
            .then();
    }

    public async navigateToListItem(listItemId: string): Promise<void> {
        const url = this.router.url;
        const urlParts = url.split('/');
        urlParts[urlParts.length - 1] = listItemId;
        await this.navigate(urlParts.join('/'), {
            replaceUrl: true
        }, true);
    }

    public async navigateToLogin(): Promise<boolean> {
        this.afterLoginNavigation.set(false);
        return this.navigate([
            'auth',
            'login'
        ]);
    }

    public async navigateToLogout(): Promise<boolean> {
        return this.navigate([
            'auth',
            'logout'
        ]);
    }

    public async navigateToMe(navigationExtras?: NavigationExtras, forceReplace: boolean = false, setHistoryItem: boolean = false): Promise<boolean> {
        let historyItem: HistoryItem | undefined;
        const link = [
            'me',
            'vaults'
        ];
        if (setHistoryItem) {
            const userId = this.authQuery.getUserId();

            historyItem = {
                path: link,
                title: 'FAST_ACCESS.ME',
                subTitle: 'VAULTS',
                icon: userId ? IconService.getUserIconUrl(userId) : undefined,
            };
        }
        return await this.navigate(
            link,
            navigationExtras,
            forceReplace,
            historyItem);
    }

    public getAfterLoginNavigationSignal(): Signal<boolean> {
        return this.afterLoginNavigation.asReadonly();
    }

    public setAfterLoginNavigation(): void {
        this.afterLoginNavigation.set(true);
    }

    public async navigateAfterLogin(): Promise<void> {
        const hasNavigated = await this.navigateToMe({
            state: {
                fromLogin: true
            }
        }, false, true);
        if (!hasNavigated) {
            throw (new NavigationError('Could not navigate to me/vaults'));
        }
        this.afterLoginNavigation.set(true);
    }

    public getInProgressDocumentPreviewLink(vaultId: string, checkedOutDocumentId: string): Array<string> {
        return [
            URL_PATH_CONFIGS.paths.vaultDetail,
            vaultId,
            'in-progress',
            'document',
            checkedOutDocumentId
        ];
    }

    public selectUrlContainsSegment(segment: string): Observable<boolean> {
        return defer(() => this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                map(() => this.router.url.includes(segment)),
                startWith(this.router.url.includes(segment))
            ));
    }

    public async updateQueryParams(queryParams: Record<string, string | undefined>, queryParamsHandling: QueryParamsHandling = 'merge', historyItem?: HistoryItem): Promise<void> {
        const hasNavigated = await this.router.navigate([], {
            queryParams,
            queryParamsHandling
        });

        if (!hasNavigated) {
            return;
        }

        if (!historyItem) {
            historyItem = this.appQuery.getLastNavigationHistoryItem();
            if (!historyItem) {
                return;
            }

            historyItem.queryParams = Object.fromEntries(
                Object.entries({ ...historyItem.queryParams, ...queryParams })
                    .filter(([, queryParamValue]) => queryParamValue !== undefined)
            ) as Record<string, string>;
        }
        this.historyService.addNavigationHistory(historyItem, historyItem.path);
    }

    public getModifiedUrlSegments({
                                      urlSegments,
                                      hasSmallLayout
                                  }: GetModifiedUrlSegmentsParams): Array<string> {
        if (urlSegments.length === 1 && this.responsiveUrlPaths.includes(urlSegments[0])) {
            // Some paths are excluded here as their respective components will adjust whenever the viewport changes.
            // Therefore, we don't need separate routes for smartphone/tablet layout and just return here.
            return urlSegments;
        }

        // The vault-detail page is a single (multi-segment) responsive component that adjusts to the viewport itself,
        // so it must not be redirected into the smartphone (/s) layout.
        if (urlSegments[0] === URL_PATH_CONFIGS.paths.vaultDetail) {
            return urlSegments;
        }

        const modifiedUrlSegments = [...urlSegments];
        // Watch out! We assume that the segment for "has small layout" is always the FIRST one.
        const hasSmallLayoutUrlSegment = URL_PATH_CONFIGS.paths.smallViewport === urlSegments[0];
        if (hasSmallLayout && !hasSmallLayoutUrlSegment) {
            modifiedUrlSegments.unshift(URL_PATH_CONFIGS.paths.smallViewport);
        } else if (!hasSmallLayout && hasSmallLayoutUrlSegment) {
            modifiedUrlSegments.shift();
        }

        return modifiedUrlSegments;
    }

    public getModifiedUrlSegmentsAsString({
                                              urlSegments,
                                              hasSmallLayout
                                          }: GetModifiedUrlSegmentsParams): string {
        return this.getModifiedUrlSegments({
                urlSegments,
                hasSmallLayout
            })
            .join('/')
            .replace('//', '/');
    }

    public async navigateToActiveVault(): Promise<void> {
        const activeVaultId = this.vaultQuery.getFirstActiveVaultId();
        if (activeVaultId) {
            await this.navigateToVault(activeVaultId);
        }
    }

    public async navigateToVault(vaultId: string): Promise<void> {
        await this.navigate([
            URL_PATH_CONFIGS.paths.vaultDetail,
            vaultId
        ]);
    }

    public async navigateToVaultInProgressDocuments(vaultId: string): Promise<void> {
        await this.navigate([
            URL_PATH_CONFIGS.paths.vaultDetail,
            vaultId,
            'in-progress'
        ], undefined, false, {
            title: 'IN_PROGRESS',
            subTitle: this.vaultQuery.getVaultNameById(vaultId),
            svg: ICONS.ITEM_CHECKOUT
        });
    }

    public async navigateToVaultTrashDocuments(vaultId: string): Promise<void> {
        await this.navigate([
            URL_PATH_CONFIGS.paths.vaultDetail,
            vaultId,
            'trash'
        ], undefined, false, {
            title: this.translateService.instant('RECYCLE_BIN'),
            subTitle: this.vaultQuery.getVaultNameById(vaultId),
            svg: ICONS.TRASH
        });
    }

    public async navigateToFileExplorer(vaultId: string): Promise<void> {
        const path = [URL_PATH_CONFIGS.paths.magnets];
        const queryParams = {
            vaultId,
            selectedTab: TabIdEnum.FOLDER
        };
        const historyItem: HistoryItem = {
            title: 'DOCUMENTS',
            subTitle: this.vaultQuery.getVaultNameById(vaultId),
            svg: ICONS.FOLDER,
            queryParams,
            path
        };

        await this.navigate(path, { queryParams }, undefined, historyItem);
    }

    public async navigateToClickFinderNode(vaultId: string, clickFinderNode: ClickFinderNodeWithIcon): Promise<void> {
        const path = [URL_PATH_CONFIGS.paths.clickFinder];
        const queryParams: Record<string, string> = vaultId ? { vaultId } : {};
        const historyItem: HistoryItem = {
            subTitle: this.vaultQuery.getVaultNameById(vaultId),
            title: clickFinderNode.caption,
            svg: clickFinderNode.icon,
            path,
            queryParams,
        };
        await this.navigate(path, { queryParams }, undefined, historyItem);
    }

    public async navigateToDefaultSearch(storedSearchItem: StoredSearchItem): Promise<void> {
        const historyItem: HistoryItem = {
            path: [storedSearchItem.path],
            subTitle: storedSearchItem.title,
            svg: storedSearchItem.svgIcon,
            title: 'STORED_SEARCHES'
        };

        await this.navigate(storedSearchItem.path, undefined, undefined, historyItem);
        this.appService.hideSearchMenu();
    }

    public async navigateToSearchQuery(searchQuery: ApiSearchQuery): Promise<void> {
        const queryParams = {
            searchQueryId: searchQuery.id,
            vaultId: searchQuery.vaultId
        };

        const historyItem: HistoryItem = {
            path: [URL_PATH_CONFIGS.paths.searchQuery],
            queryParams: queryParams,
            subTitle: this.vaultQuery.getVaultNameById(searchQuery.vaultId),
            svg: searchQuery.visibility === SearchQueryVisibilityEnum.PERSONAL ? ICONS.STOREDSEARCH_PERSONAL : ICONS.STOREDSEARCH_PUBLIC,
            title: searchQuery.name
        };

        await this.navigate(URL_PATH_CONFIGS.paths.searchQuery, { queryParams }, undefined, historyItem);
        this.appService.hideSearchMenu();
    }

    public async navigateToSearch(searchTerm: string, vault?: Vault): Promise<void> {
        const searchScope = vault ? SearchScopeEnum.LOCAL : SearchScopeEnum.GLOBAL;
        const queryParams: Record<string, string> = {
            searchScope,
            searchTerm
        };
        if (vault) {
            queryParams['vaultId'] = vault.id;
        }

        let historyItem: HistoryItem;
        if (vault) {
            historyItem = {
                path: [URL_PATH_CONFIGS.paths.search],
                queryParams: queryParams,
                subTitle: this.translateService.instant('SEARCH.KEYWORD_IN_VAULT_TITLE',
                    {
                        keyword: searchTerm,
                        vault: vault.name
                    }),
                svg: ICONS.SEARCH_LOCAL,
                title: 'SEARCH.TITLE'
            };
        } else {
            historyItem = {
                path: [URL_PATH_CONFIGS.paths.search],
                queryParams: queryParams,
                subTitle: searchTerm,
                svg: ICONS.SEARCH_OVERALL,
                title: 'SEARCH.GLOBAL_TITLE'
            };
        }

        await this.navigate(URL_PATH_CONFIGS.paths.search, { queryParams }, undefined, historyItem);
        this.appService.hideSearchMenu();
    }

    private getCurrentUrl(): string {
        const currentUrlSegments = this.router.url.split(this.urlSeparator)
            .filter(Boolean);
        return this.urlSeparator + this.getModifiedUrlSegmentsAsString({
            urlSegments: currentUrlSegments,
            hasSmallLayout: false // Remove URL segment that indicates a small layout
        });
    }

    private getLastNavigationUrl(path: Array<string>, queryParams?: Record<string, string>): string {
        let url = (this.urlSeparator + path.join(this.urlSeparator))
            .replaceAll(`${this.urlSeparator}${this.urlSeparator}`, this.urlSeparator);

        if (queryParams) {
            url += '?' + Object.entries(queryParams)
                .map(queryParam => queryParam[0] + '=' + queryParam[1])
                .join('&');
        }

        return url;
    }

    private async triggerNavigation(url: Array<string>, navigationExtras?: NavigationExtras, historyItem?: HistoryItem): Promise<boolean> {
        const hasNavigated = await this.router.navigate(url, navigationExtras);
        if (!hasNavigated) {
            return false;
        }

        if (historyItem) {
            this.historyService.addNavigationHistory(historyItem, url);
        }

        return true;
    }
}
